import Modal from '@risk-smart/themed-cloudscape-components/modal';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { WebhookPartsFragment } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import WebhookForm from '../forms/WebhookForm';
import type { WebhookFormFields } from '../forms/webhookSchema';
import {
  ALL_EVENTS_BACKEND_KEY,
  ALL_EVENTS_SENTINEL,
  isPersistedStatus,
} from '../forms/webhookSchema';
import WebhookSecretReveal from '../forms/WebhookSecretReveal';
import { generateWebhookSecret } from './webhookSecret';

const mapWebhookToFormFields = (
  webhook: WebhookPartsFragment,
  allEventsLabel: string
): Partial<WebhookFormFields> => {
  // If the webhook is subscribed to the wildcard event-type, open in "All
  // events" mode with the sentinel as its single token. Otherwise map each
  // subscription to a regular option.
  const hasWildcardSub = webhook.subscriptions.some(
    (s) => s.eventType.EventType === ALL_EVENTS_BACKEND_KEY
  );

  return {
    Title: webhook.Title,
    WebhookUrl: webhook.WebhookUrl,
    Description: webhook.Description ?? undefined,
    // Toggle is binary active/paused; 'failed' maps to 'paused' so the toggle
    // shows OFF and the user has to explicitly flip to active to recover.
    Status: webhook.Status === 'active' ? 'active' : 'paused',
    EventMode: hasWildcardSub ? 'all' : 'select',
    EventTypes: hasWildcardSub
      ? [{ value: ALL_EVENTS_SENTINEL, label: allEventsLabel }]
      : webhook.subscriptions.map((s) => ({
          value: s.WebhookEventId,
          label: s.eventType.EventType,
        })),
  };
};

interface Props {
  /** When provided, the modal is in edit mode for this webhook. */
  webhook?: WebhookPartsFragment;
  onDismiss: () => void;
  onSuccess: () => void;
}

type Step = 'form' | 'secret';

export const WebhookFormModal = ({ webhook, onDismiss, onSuccess }: Props) => {
  const isEdit = !!webhook;
  const i18nPrefix = isEdit ? 'webhooks.edit_modal' : 'webhooks.create_modal';

  const { t } = useTranslation(['common'], { keyPrefix: i18nPrefix });
  const { t: secretT } = useTranslation(['common'], {
    keyPrefix: 'webhooks.secret_reveal',
  });
  const { t: formT } = useTranslation(['common'], {
    keyPrefix: 'webhooks.form',
  });
  const { addNotification } = useNotifications();

  const [step, setStep] = useState<Step>('form');
  const [secret, setSecret] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);

  // Holds the latest UseFormReturn so handleAttemptDismiss can read isDirty
  // synchronously without lifting the value into parent state on every keystroke.
  const formRef = useRef<UseFormReturn<WebhookFormFields> | null>(null);

  const allEventsLabel = formT('events_mode_all');
  const defaultValues = useMemo(
    () =>
      webhook ? mapWebhookToFormFields(webhook, allEventsLabel) : undefined,
    [webhook, allEventsLabel]
  );

  const currentStatus =
    webhook && isPersistedStatus(webhook.Status) ? webhook.Status : undefined;

  const handleSubmit = async (_values: WebhookFormFields) => {
    setSubmitting(true);
    try {
      // Backend mutation will be wired up in a follow-up. For create, generate
      // a placeholder signing secret to drive the reveal flow.
      if (isEdit) {
        addNotification({ type: 'success', content: t('success_message') });
        onSuccess();

        return;
      }
      setSecret(generateWebhookSecret());
      setStep('secret');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSecretConfirm = () => {
    addNotification({ type: 'success', content: t('success_message') });
    onSuccess();
  };

  const handleAttemptDismiss = () => {
    if (isEdit && formRef.current?.formState.isDirty) {
      setShowDiscard(true);

      return;
    }
    onDismiss();
  };

  if (step === 'secret') {
    return (
      <Modal
        visible
        // The signing secret is shown only once; X / Esc / overlay-click are
        // intentionally no-ops to prevent accidental dismissal. The user must
        // click Done to acknowledge.
        onDismiss={() => undefined}
        header={secretT('title')}
        size={'medium'}
      >
        <WebhookSecretReveal secret={secret} onConfirm={handleSecretConfirm} />
      </Modal>
    );
  }

  return (
    <>
      <Modal
        visible
        onDismiss={handleAttemptDismiss}
        header={t('title')}
        size={'medium'}
      >
        <WebhookForm
          loading={submitting}
          defaultValues={defaultValues}
          currentStatus={currentStatus}
          onSubmit={handleSubmit}
          renderActions={(form) => {
            // Cheap ref write per render; consumed by handleAttemptDismiss.
            formRef.current = form;

            return (
              <SpaceBetween direction={'horizontal'} size={'xs'}>
                <Button
                  variant={'link'}
                  formAction={'none'}
                  onClick={handleAttemptDismiss}
                  disabled={submitting}
                >
                  {t('cancel_button')}
                </Button>
                <Button
                  variant={'primary'}
                  formAction={'submit'}
                  loading={submitting}
                  // The submit handler runs validation on click; we don't pre-
                  // disable on isValid (avoids stuck-disabled-after-error UX
                  // under react-hook-form's onTouched mode). Edit mode still
                  // requires the form to be dirty to enable.
                  disabled={isEdit && !form.formState.isDirty}
                >
                  {t('submit_button')}
                </Button>
              </SpaceBetween>
            );
          }}
        />
      </Modal>
      {showDiscard && (
        <Modal
          visible
          onDismiss={() => setShowDiscard(false)}
          header={t('discard_title')}
          size={'small'}
          footer={
            <SpaceBetween direction={'horizontal'} size={'xs'}>
              <Button variant={'link'} onClick={() => setShowDiscard(false)}>
                {t('discard_cancel')}
              </Button>
              <Button
                variant={'primary'}
                onClick={() => {
                  setShowDiscard(false);
                  onDismiss();
                }}
              >
                {t('discard_confirm')}
              </Button>
            </SpaceBetween>
          }
        >
          {t('discard_message')}
        </Modal>
      )}
    </>
  );
};

export default WebhookFormModal;
